// CL-1 errorClassifier (omo F-7 영감).
//
// OpenAI SDK error 객체를 symptom-based classify → recovery dispatch 진입점.
//
// 분류 type:
//   - rate_limit               (status=429 / "rate limit" / "too many requests")
//   - context_limit            (code='context_length_exceeded' / "context length" / "maximum context")
//   - tool_result_missing      ("tool_calls" + "must be followed by tool messages")
//   - empty_message            ("empty" + ("message" | "content"))
//   - invalid_function_call    (code/type 'invalid_function_call' / "function call" + "invalid")
//   - invalid_schema           (code/type 'invalid_schema' / "schema" + "invalid")
//   - null                     (분류 불가)
//
// Recursive probe — err.message + err.error.message + err.response.data.error.message
//
// 의존성: 없음 (pure module)

export const ERROR_TYPES = Object.freeze({
  RATE_LIMIT: 'rate_limit',
  CONTEXT_LIMIT: 'context_limit',
  TOOL_RESULT_MISSING: 'tool_result_missing',
  EMPTY_MESSAGE: 'empty_message',
  INVALID_FUNCTION_CALL: 'invalid_function_call',
  INVALID_SCHEMA: 'invalid_schema',
});

/**
 * @param {*} err  — OpenAI SDK error 또는 임의 throw 객체
 * @returns {string|null}  ERROR_TYPES 중 하나 또는 null
 */
export function classifyOpenAIError(err) {
  if (err === null || err === undefined) return null;
  // primitive (string/number) error도 message 추출 후 진행
  if (typeof err !== 'object') {
    const msg = String(err).toLowerCase();
    return _classifyByMessage(null, '', '', msg);
  }

  const status = _resolveStatus(err);
  const code = (err.code ?? '').toString().toLowerCase();
  const type = (err.type ?? '').toString().toLowerCase();
  const msg = _extractMessage(err).toLowerCase();

  return _classifyByMessage(status, code, type, msg);
}

function _classifyByMessage(status, code, type, msg) {
  // ────────────────────────────────────────
  // rate_limit
  // ────────────────────────────────────────
  if (status === 429 || msg.includes('rate limit') || msg.includes('too many requests')) {
    return ERROR_TYPES.RATE_LIMIT;
  }

  // ────────────────────────────────────────
  // context_limit
  // ────────────────────────────────────────
  if (
    code === 'context_length_exceeded' ||
    msg.includes('context length') ||
    msg.includes('maximum context')
  ) {
    return ERROR_TYPES.CONTEXT_LIMIT;
  }

  // ────────────────────────────────────────
  // tool_result_missing — OpenAI 400 tool_calls orphan
  // ────────────────────────────────────────
  if (msg.includes('tool_calls') && msg.includes('must be followed by tool messages')) {
    return ERROR_TYPES.TOOL_RESULT_MISSING;
  }

  // ────────────────────────────────────────
  // invalid_function_call — 분류 우선 (schema보다 좁은 match)
  // ────────────────────────────────────────
  if (
    code === 'invalid_function_call' ||
    type === 'invalid_function_call' ||
    (msg.includes('function call') && msg.includes('invalid'))
  ) {
    return ERROR_TYPES.INVALID_FUNCTION_CALL;
  }

  // ────────────────────────────────────────
  // invalid_schema
  // ────────────────────────────────────────
  if (
    code === 'invalid_schema' ||
    type === 'invalid_schema' ||
    (msg.includes('schema') && msg.includes('invalid'))
  ) {
    return ERROR_TYPES.INVALID_SCHEMA;
  }

  // ────────────────────────────────────────
  // empty_message — 마지막에 검사 (가장 광범위)
  // ────────────────────────────────────────
  if (msg.includes('empty') && (msg.includes('message') || msg.includes('content'))) {
    return ERROR_TYPES.EMPTY_MESSAGE;
  }

  return null;
}

// ────────────────────────────────────────────────
// internal helpers
// ────────────────────────────────────────────────

function _resolveStatus(err) {
  if (typeof err.status === 'number') return err.status;
  if (typeof err.statusCode === 'number') return err.statusCode;
  if (err.response && typeof err.response.status === 'number') return err.response.status;
  return null;
}

function _extractMessage(err) {
  const parts = [];
  if (typeof err.message === 'string' && err.message) parts.push(err.message);
  if (err.error && typeof err.error.message === 'string' && err.error.message) {
    parts.push(err.error.message);
  }
  if (err.response?.data?.error?.message && typeof err.response.data.error.message === 'string') {
    parts.push(err.response.data.error.message);
  }
  // some SDKs expose err.body or err.cause
  if (err.body && typeof err.body === 'object' && typeof err.body.message === 'string') {
    parts.push(err.body.message);
  }
  return parts.join(' ');
}

export const _extractMessageForTest = _extractMessage;
export const _resolveStatusForTest = _resolveStatus;
